import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useRef } from 'react';

import { type Entity, identityEntityGetEntityOptions } from '@column/api';
import { Table, TableColumn, TableVariant } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { EmptyState, Loading } from '~/components';
import { RelativeTime } from '~/elements';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { CardProgramRepository } from '~/repositories/CardProgramRepository';
import { Card, CardRepository } from '~/repositories/CardRepository';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { formatString } from '~/util';

const formatExpires = (card: Card): string => card.expiresAt || '-';

export const PageCardsOverview: React.FC = () => {
  const navigate = useNavigate();
  const { currentPlatform, isSandbox } = useSessionStore();
  const {
    cursor,
    hasPrevious,
    onNext,
    onPrevious,
    pageIndex,
    pageSize,
    reset: resetPagination,
    setPageSize,
  } = usePaginationState();

  // Reset the cursor when the platform or sandbox mode changes; otherwise the
  // old record id gets sent as starting_after against a different dataset.
  const isInitialRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }
    resetPagination();
  }, [currentPlatform?.id, isSandbox, resetPagination]);

  const {
    data: cardsData,
    isLoading: isLoadingCards,
    isError: isCardsError,
  } = useQuery({
    queryKey: ['cards', 'list', currentPlatform?.id, isSandbox, pageSize, cursor],
    queryFn: () => CardRepository.listCards({ limit: pageSize, startingAfter: cursor }),
    enabled: Boolean(currentPlatform?.id),
    placeholderData: keepPreviousData,
  });

  const { data: programsData } = useQuery({
    queryKey: ['card-programs', 'list-all', currentPlatform?.id, isSandbox],
    queryFn: () => CardProgramRepository.listCardPrograms(),
    enabled: Boolean(currentPlatform?.id),
  });

  const cards = cardsData?.cards ?? [];
  const hasMore = Boolean(cardsData?.hasMore);

  const programsById = useMemo(() => {
    const map = new Map<string, string>();
    (programsData?.cardPrograms ?? []).forEach((program) => {
      if (program.description) {
        map.set(program.id, program.description);
      }
    });
    return map;
  }, [programsData]);

  const uniqueEntityIds = useMemo(
    () => Array.from(new Set(cards.map((c) => c.authorizedUserEntityId).filter((id): id is string => Boolean(id)))),
    [cards]
  );

  const entityQueries = useQueries({
    queries: uniqueEntityIds.map((entityId) => ({
      ...identityEntityGetEntityOptions({ path: { entity_id: entityId } }),
      enabled: Boolean(currentPlatform?.id),
    })),
  });

  const entitiesById = useMemo(() => {
    const map = new Map<string, Entity>();
    entityQueries.forEach((query, i) => {
      if (query.data) map.set(uniqueEntityIds[i], query.data);
    });
    return map;
  }, [entityQueries, uniqueEntityIds]);

  const getAuthorizedUserName = (entityId: string): string => {
    const entity = entitiesById.get(entityId);
    const first = entity?.person_details?.first_name ?? '';
    const last = entity?.person_details?.last_name ?? '';
    const full = `${first} ${last}`.trim();
    return full || entity?.name || '-';
  };

  const columns: TableColumn<Card>[] = [
    {
      header: 'Authorized User',
      accessor: 'authorizedUserEntityId',
      width: { min: 'min-content', max: '1fr' },
      cell: ({ row }) => <Table.TextCell weight="medium" value={getAuthorizedUserName(row.authorizedUserEntityId)} />,
    },
    {
      header: 'Card Program',
      accessor: 'cardProgramId',
      width: { min: 'min-content', max: '1fr' },
      cell: ({ row }) => <Table.TextCell value={programsById.get(row.cardProgramId) || row.cardProgramId || '-'} />,
    },
    {
      header: 'BIN',
      accessor: 'bin',
      width: { min: 'min-content', max: 'min-content' },
      cell: ({ value }) => <Table.TextCell value={(value as string) || '-'} />,
    },
    {
      header: 'Last 4',
      accessor: 'lastFourDigits',
      width: { min: 'min-content', max: 'min-content' },
      cell: ({ value }) => <Table.TextCell value={(value as string) || '-'} />,
    },
    {
      header: 'Expires',
      id: 'expires',
      width: { min: 'min-content', max: 'min-content' },
      cell: ({ row }) => <Table.TextCell value={formatExpires(row)} />,
    },
    {
      header: 'Status',
      accessor: 'status',
      width: { min: 'min-content', max: 'min-content' },
      cell: ({ value }) => <Table.ChipCell value={value ? formatString(value as string) : '-'} />,
    },
    {
      header: 'Type',
      accessor: 'type',
      width: { min: 'min-content', max: 'min-content' },
      cell: ({ value }) => <Table.ChipCell value={value ? formatString(value as string) : '-'} />,
    },
    {
      header: 'Created',
      accessor: 'createdAt',
      width: { min: 'min-content', max: 'min-content' },
      cell: ({ value }) => <Table.Cell>{value ? <RelativeTime timestamp={value as Date} /> : '-'}</Table.Cell>,
    },
  ];

  return (
    <>
      <Loading isLoading={isLoadingCards}>
        <Inner pt={0} px="var(--page-margin)">
          {isCardsError ? (
            <EmptyState headline="Error" text="Failed to load cards." />
          ) : cards.length === 0 && !hasPrevious ? (
            <EmptyState headline="No cards" text="No cards found." />
          ) : (
            <Table<Card>
              columns={columns}
              data={cards}
              variant={TableVariant.List}
              pagination={{
                hasNext: hasMore,
                hasPrevious,
                pageIndex,
                pageSize,
                onNext: () => {
                  if (cards.length > 0) {
                    onNext({ cursor: cards[cards.length - 1].id });
                  }
                },
                onPrevious,
                onPageSizeChange: setPageSize,
              }}
              onRowClick={(row) => {
                navigate(`${ROUTE.CARDS}/edit/${row.id}`);
              }}
            />
          )}
        </Inner>
      </Loading>
    </>
  );
};
